import React, { useState, useEffect } from 'react';

const TaskManager = ({ onTaskSelect, selectedTask }) => {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    subject: '',
    estimatedTime: 25,
    dueDate: '',
    tags: []
  });
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [showAddTask, setShowAddTask] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    const savedTasks = localStorage.getItem('studyTasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }
  }, []);

  const saveTasks = (updatedTasks) => {
    setTasks(updatedTasks);
    localStorage.setItem('studyTasks', JSON.stringify(updatedTasks));
  };

  const addTask = () => {
    if (!newTask.title.trim()) return;

    const task = {
      id: Date.now(),
      ...newTask,
      tags: tagInput.split(',').map(tag => tag.trim()).filter(tag => tag),
      completed: false,
      completedAt: null,
      timeSpent: 0,
      sessions: [],
      createdAt: new Date().toISOString()
    };

    saveTasks([...tasks, task]);
    setNewTask({
      title: '',
      description: '',
      priority: 'medium',
      subject: '',
      estimatedTime: 25,
      dueDate: '',
      tags: []
    });
    setTagInput('');
    setShowAddTask(false);
  };

  const toggleTask = (taskId) => {
    const updatedTasks = tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          completed: !task.completed,
          completedAt: !task.completed ? new Date().toISOString() : null
        };
      }
      return task;
    });
    saveTasks(updatedTasks);
  };

  const deleteTask = (taskId) => {
    saveTasks(tasks.filter(task => task.id !== taskId));
  };

  const updateTaskTime = (taskId, sessionTime) => {
    const updatedTasks = tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          timeSpent: task.timeSpent + sessionTime,
          sessions: [...task.sessions, {
            duration: sessionTime,
            completedAt: new Date().toISOString()
          }]
        };
      }
      return task;
    });
    saveTasks(updatedTasks);
  };

  // Expose this function to parent component
  React.useEffect(() => {
    window.updateTaskTime = updateTaskTime;
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getPriorityWeight = (priority) => {
    switch (priority) {
      case 'high': return 3;
      case 'medium': return 2;
      case 'low': return 1;
      default: return 0;
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getFilteredTasks = () => {
    let filtered = [...tasks];

    // Apply filters
    switch (filter) {
      case 'active':
        filtered = filtered.filter(task => !task.completed);
        break;
      case 'completed':
        filtered = filtered.filter(task => task.completed);
        break;
      case 'overdue':
        filtered = filtered.filter(task => !task.completed && isOverdue(task.dueDate));
        break;
      case 'today':
        const today = new Date().toDateString();
        filtered = filtered.filter(task => {
          if (!task.dueDate) return false;
          return new Date(task.dueDate).toDateString() === today;
        });
        break;
      default:
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          if (a.completed !== b.completed) {
            return a.completed ? 1 : -1;
          }
          return getPriorityWeight(b.priority) - getPriorityWeight(a.priority);
        case 'dueDate':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate) - new Date(b.dueDate);
        case 'timeSpent':
          return b.timeSpent - a.timeSpent;
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const filteredTasks = getFilteredTasks();
  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.completed).length,
    overdue: tasks.filter(t => !t.completed && isOverdue(t.dueDate)).length,
    totalTime: tasks.reduce((sum, t) => sum + t.timeSpent, 0)
  };

  return (
    <div className="task-manager">
      <div className="task-header">
        <div className="task-title">
          <h2>Task Manager</h2>
          <button
            onClick={() => setShowAddTask(!showAddTask)}
            className="add-task-btn"
          >
            + Add Task
          </button>
        </div>

        <div className="task-stats">
          <div className="stat-item">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.overdue}</span>
            <span className="stat-label">Overdue</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{formatTime(stats.totalTime)}</span>
            <span className="stat-label">Time Spent</span>
          </div>
        </div>
      </div>

      {showAddTask && (
        <div className="add-task-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="Task title *"
              value={newTask.title}
              onChange={(e) => setNewTask({...newTask, title: e.target.value})}
              className="task-input"
            />
            <select
              value={newTask.priority}
              onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
              className="priority-select"
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
            </select>
          </div>

          <textarea
            placeholder="Description (optional)"
            value={newTask.description}
            onChange={(e) => setNewTask({...newTask, description: e.target.value})}
            className="task-description"
            rows="2"
          />

          <div className="form-row">
            <input
              type="text"
              placeholder="Subject"
              value={newTask.subject}
              onChange={(e) => setNewTask({...newTask, subject: e.target.value})}
              className="task-input"
            />
            <input
              type="number"
              placeholder="Est. time (min)"
              value={newTask.estimatedTime}
              onChange={(e) => setNewTask({...newTask, estimatedTime: parseInt(e.target.value) || 25})}
              className="task-input"
              min="1"
              max="480"
            />
            <input
              type="date"
              value={newTask.dueDate}
              onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
              className="task-input"
            />
          </div>

          <input
            type="text"
            placeholder="Tags (comma separated)"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            className="task-input"
          />

          <div className="form-actions">
            <button onClick={addTask} className="save-btn">Save Task</button>
            <button onClick={() => setShowAddTask(false)} className="cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      <div className="task-controls">
        <div className="filter-section">
          <label>Filter:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Tasks</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due Today</option>
          </select>
        </div>

        <div className="sort-section">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="priority">Priority</option>
            <option value="dueDate">Due Date</option>
            <option value="timeSpent">Time Spent</option>
            <option value="created">Created</option>
          </select>
        </div>
      </div>

      <div className="tasks-list">
        {filteredTasks.length === 0 ? (
          <div className="no-tasks">
            <p>No tasks found.</p>
            <p>Create your first task to get started!</p>
          </div>
        ) : (
          filteredTasks.map(task => (
            <div
              key={task.id}
              className={`task-item ${task.completed ? 'completed' : ''} ${selectedTask?.id === task.id ? 'selected' : ''}`}
              onClick={() => onTaskSelect && onTaskSelect(task)}
            >
              <div className="task-main">
                <div className="task-checkbox">
                  <input
                    type="checkbox"
                    checked={task.completed}
                    onChange={() => toggleTask(task.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div className="task-content">
                  <div className="task-title-row">
                    <h4 className="task-name">{task.title}</h4>
                    <div
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(task.priority) }}
                    >
                      {task.priority}
                    </div>
                  </div>

                  {task.description && (
                    <p className="task-desc">{task.description}</p>
                  )}

                  <div className="task-meta">
                    {task.subject && <span className="task-subject">{task.subject}</span>}
                    {task.estimatedTime && (
                      <span className="estimated-time">
                        Est: {formatTime(task.estimatedTime)}
                      </span>
                    )}
                    {task.timeSpent > 0 && (
                      <span className="time-spent">
                        Spent: {formatTime(task.timeSpent)}
                      </span>
                    )}
                    {task.dueDate && (
                      <span className={`due-date ${isOverdue(task.dueDate) ? 'overdue' : ''}`}>
                        Due: {new Date(task.dueDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {task.tags.length > 0 && (
                    <div className="task-tags">
                      {task.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}

                  {task.sessions.length > 0 && (
                    <div className="task-progress">
                      <span className="sessions-count">{task.sessions.length} sessions</span>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${Math.min((task.timeSpent / task.estimatedTime) * 100, 100)}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="task-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTask(task.id);
                    }}
                    className="delete-btn"
                    title="Delete task"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskManager;